import { useEffect, useState, type FormEvent } from "react";
import { fetchAllRecords, type AllRecordsReport } from "../lib/all-records";
import { trackFeature } from "../lib/featureAnalytics";

/** Short note on what each type is for, so the table teaches as well as reports. */
const TYPE_NOTE: Record<string, string> = {
  A: "IPv4 address",
  AAAA: "IPv6 address",
  CNAME: "alias to another name",
  MX: "mail servers",
  NS: "authoritative nameservers",
  TXT: "free-form text (SPF, verification)",
  SOA: "zone authority + serial",
  CAA: "which CAs may issue certificates",
  SRV: "service location",
  DS: "DNSSEC delegation signer",
  DNSKEY: "DNSSEC public key",
  SVCB: "service binding (RFC 9460)",
  HTTPS: "HTTPS service binding, ALPN, ECH",
};

export function DnsLookup() {
  const [host, setHost] = useState("");
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState<AllRecordsReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showEmpty, setShowEmpty] = useState(false);

  const run = (raw: string) => {
    const h = normalizeHost(raw);
    if (!h) return;
    setHost(h);
    setBusy(true);
    setReport(null);
    setError(null);
    history.replaceState(null, "", `?${new URLSearchParams({ host: h })}`);
    fetchAllRecords(h)
      .then((r) => setReport(r))
      .catch((e) => setError(String(e?.message ?? e)))
      .finally(() => setBusy(false));
  };

  useEffect(() => {
    const h = new URLSearchParams(window.location.search).get("host");
    if (h) run(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    run(host);
  };

  // Default to only the types that actually returned something — a wall of
  // 13 rows where 9 say "none" buries the answer.
  const rows = report
    ? report.entries.filter((e) => showEmpty || (e.status === "ok" && e.records.length > 0))
    : [];
  const emptyCount = report ? report.entries.length - report.entries.filter((e) => e.status === "ok" && e.records.length > 0).length : 0;

  return (
    <main className="console">
      <div className="input-row" role="search">
        <span className="input-row-label">LOOKUP</span>
        <form onSubmit={submit} className="lookup-form">
          <input
            type="text"
            placeholder="example.com"
            value={host}
            onChange={(e) => setHost(e.target.value)}
            autoFocus
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            aria-label="Domain or hostname"
          />
          <button type="submit" disabled={busy || !host.trim()}>
            {busy ? "Looking up…" : "Look up"}
          </button>
        </form>
        <span className="input-row-hint" aria-hidden="true">
          <span className="stat-dot" /> every record type at once
        </span>
      </div>

      {error ? <div className="error" role="alert">{error}</div> : null}

      <div className="body-grid body-grid-single">
        <section className="table-panel">
          <div className="panel-title">
            ALL DNS RECORDS
            {report ? <b> · {report.domain}</b> : null}
          </div>

          {!report && !busy ? (
            <div className="empty-state">
              <p>
                Queries every common record type in one go — A, AAAA, CNAME, MX, NS, TXT, SOA, CAA,
                SRV, DS, DNSKEY, SVCB and HTTPS — and shows exactly what's published.
              </p>
              <p className="empty-hint">Try <code>cloudflare.com</code>, <code>google.com</code>, or your own domain.</p>
            </div>
          ) : null}

          {busy ? <div className="empty-state"><p>Querying every record type…</p></div> : null}

          {report ? (
            report.nxdomain ? (
              <div className="empty-state">
                <p><strong>{report.domain}</strong> returned NXDOMAIN for every record type — the name doesn't exist.</p>
                <p className="empty-hint">Check for a typo, or whether the domain is registered.</p>
              </div>
            ) : (
              <>
                <p className="dnslookup-summary">
                  {report.present.length
                    ? <>Found <strong>{report.present.length}</strong> record type{report.present.length === 1 ? "" : "s"}: {report.present.join(", ")}.</>
                    : <>The name resolves but publishes none of the queried record types.</>}
                </p>

                <table className="health-table">
                  <thead>
                    <tr><th>Type</th><th>Records</th></tr>
                  </thead>
                  <tbody>
                    {rows.map((e) => (
                      <tr key={e.type} className={`health-row health-${e.status}`}>
                        <td className="ht-type">
                          <code>{e.type}</code>
                          <span className="deleg-note">{TYPE_NOTE[e.type] ?? ""}</span>
                          <a className="record-track-link" href={`/dns-change-tracker?${new URLSearchParams({ host: report.domain, type: e.type })}`}
                            onClick={() => trackFeature("change_tracker", "all_records")} aria-label={`Track ${e.type} records`}>Track record</a>
                        </td>
                        <td className="ht-records">
                          {e.records.length ? (
                            <ul>{e.records.map((r, i) => <li key={i}><code>{r}</code></li>)}</ul>
                          ) : (
                            <span className="ht-empty">
                              {e.status === "ok" ? "none published" : e.status === "servfail" ? "SERVFAIL" : e.status}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {emptyCount > 0 ? (
                  <p className="dnslookup-toggle">
                    <button type="button" className="linklike" onClick={() => setShowEmpty((s) => !s)}>
                      {showEmpty ? "Hide" : `Show ${emptyCount}`} record type{emptyCount === 1 ? "" : "s"} with no records
                    </button>
                  </p>
                ) : null}

                <p className="whois-cross">
                  This is one resolver's view. To see whether a record has propagated worldwide, run it
                  across all 14: <a href={`/?host=${encodeURIComponent(report.domain)}&type=A`}>A</a> ·{" "}
                  <a href={`/?host=${encodeURIComponent(report.domain)}&type=MX`}>MX</a> ·{" "}
                  <a href={`/?host=${encodeURIComponent(report.domain)}&type=NS`}>NS</a>. Or check its{" "}
                  <a href={`/dns-health?domain=${encodeURIComponent(report.domain)}`}>DNS health</a> ·{" "}
                  <a href={`/whois-lookup?domain=${encodeURIComponent(report.domain)}`}>WHOIS</a>.
                </p>
              </>
            )
          ) : null}
        </section>
      </div>
    </main>
  );
}

function normalizeHost(raw: string): string {
  return raw.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "").replace(/\.$/, "");
}
